import { useState, useEffect } from 'react';
import { EVENT_START_TIME } from '../constants/eventConfig';

interface PreEventCountdown {
  isPreEvent: boolean;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

/**
 * Hook to manage pre-event countdown timer
 * 
 * Calculates time remaining until EVENT_START_TIME and updates every second.
 * Returns isPreEvent: false when the event has started.
 */
export function usePreEventCountdown(): PreEventCountdown {
  const [countdown, setCountdown] = useState<PreEventCountdown>(() => {
    return calculateCountdown();
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(calculateCountdown());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return countdown;
}

function calculateCountdown(): PreEventCountdown {
  const now = new Date();
  const timeRemaining = EVENT_START_TIME.getTime() - now.getTime();

  // Event has started or passed
  if (timeRemaining <= 0) {
    return {
      isPreEvent: false,
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    };
  }

  // Calculate time components
  const totalSeconds = Math.floor(timeRemaining / 1000);
  const days = Math.floor(totalSeconds / (24 * 60 * 60));
  const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;

  return {
    isPreEvent: true,
    days,
    hours,
    minutes,
    seconds,
  };
}
